import {
  buildCommunityGuidelinesURI,
  useConfig,
  useLogger,
  type ValidationError,
} from '@seek/libs-shared';
import { Stack, Text, TextLink } from 'braid-design-system';

import { useTranslations } from '@vocab/react';
import { useEffect } from 'react';
import {
  CURRENT_PAGE_YOUR_FEEDBACK,
  trackClickYourFeedbackPageContinue,
  trackClickYourFeedbackRating,
  trackDisplayPage,
} from '../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayout } from '../components/ReviewPageLayout/ReviewPageLayout';
import type { Rating } from '../components/StarSelector/StarSelector';
import { CareerOpportunityRatingQuestion } from '../questions/RatingQuesions/CareerOpportunityRatingQuestion';
import { DiversityRatingQuestion } from '../questions/RatingQuesions/DiversityRatingQuestion';
import { ExecutiveManagementRatingQuestion } from '../questions/RatingQuesions/ExecutiveManagementRatingQuestion';
import { PerksAndBenefitsRatingQuestion } from '../questions/RatingQuesions/PerksAndBenefitsRatingQuestion';
import { WorkEnvironmentRatingQuestion } from '../questions/RatingQuesions/WorkEnvironmentRatingQuestion';
import { WorkLifeBalanceRatingQuestion } from '../questions/RatingQuesions/WorkLifeBalanceRatingQuestion';
import { Progress, type PageProps, type ReviewPayload } from '../type';
import translations from './.vocab';

type Props = PageProps & {
  isSubmitFailed: boolean;
  isSubmitting: boolean;
  formErrors?: ValidationError[];
};

export const YourFeedbackPage = ({
  review,
  reviewFormContext,
  setter,
  setPage,
  onSubmit,
  onAlertClosed,
  pageNumber,
  formErrors,
  isSubmitFailed,
  isSubmitting,
}: Props) => {
  const config = useConfig();
  const { t } = useTranslations(translations);
  const { logger } = useLogger();

  useEffect(() => {
    if (reviewFormContext?.variant)
      trackDisplayPage({
        config,
        context: reviewFormContext,
        currentPage: CURRENT_PAGE_YOUR_FEEDBACK,
        variant: reviewFormContext.variant,
      }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormContext?.variant]);

  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      switch (updateProperty) {
        case 'careerOpportunityRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'career_development',
          });
          break;

        case 'workLifeBalanceRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'work_life_balance',
          });
          break;
        case 'workEnvironmentRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'working_environment',
          });
          break;
        case 'executiveManagementRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'management',
          });
          break;
        case 'perksAndBenefitsRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'perks_and_benefits',
          });
          break;
        case 'diversityRating':
          trackClickYourFeedbackRating({
            config,
            context: reviewFormContext,
            currentPageSubsection: 'diversity_and_equal_opportunity',
          });
          break;
      }

      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  const handleOnContinue = () => {
    trackClickYourFeedbackPageContinue({
      config,
      context: reviewFormContext,
      validation: 'success',
    });

    if (onSubmit) {
      onSubmit();
    }
  };

  const handleOnBack = () => {
    setPage(Progress.YourSalary);
  };

  // This should never happen, but if it does, we want to show the user and log the error
  const hasFormErrors = formErrors && formErrors.length > 0;

  const formErrorComponent = hasFormErrors && (
    <Stack space="small">
      <Text tone="critical" weight="strong">
        {t('Could not submit review due to the following errors:')}
      </Text>
      {formErrors.map((error) => (
        <Text key={error.fieldName} tone="critical">
          {`${error.fieldName} - ${error.message}`}
        </Text>
      ))}
    </Stack>
  );

  if (hasFormErrors) {
    logger.error(
      {
        cause: {
          formErrors,
          review,
        },
      },
      'Review form validation throws unexpected error',
    );
  }

  return (
    <ReviewPageLayout
      progress={pageNumber}
      companyName={review?.companyName}
      onContinue={handleOnContinue}
      onBack={handleOnBack}
      onAlertClosed={isSubmitFailed ? onAlertClosed : undefined}
      isContinueLoading={isSubmitting}
      testid="ratings-page"
      alertText={t(
        "We can't create your review right now. We're doing our best to fix this. Try refreshing the page or check back later.",
      )}
    >
      <Stack space="large">
        <CareerOpportunityRatingQuestion
          onChange={handleOnChange('careerOpportunityRating')}
          value={review?.careerOpportunityRating}
        />
        <WorkLifeBalanceRatingQuestion
          onChange={handleOnChange('workLifeBalanceRating')}
          value={review?.workLifeBalanceRating}
        />
        <WorkEnvironmentRatingQuestion
          onChange={handleOnChange('workEnvironmentRating')}
          value={review?.workEnvironmentRating}
        />
        <ExecutiveManagementRatingQuestion
          onChange={handleOnChange('executiveManagementRating')}
          value={review?.executiveManagementRating}
        />
        <PerksAndBenefitsRatingQuestion
          onChange={handleOnChange('perksAndBenefitsRating')}
          value={review?.perksAndBenefitsRating}
        />
        <DiversityRatingQuestion
          onChange={handleOnChange('diversityRating')}
          value={review?.diversityRating}
        />
        <Text size="small" tone="neutral">
          {t(
            'By submitting your review, you confirm that your review complies with our <TextLink>Company Reviews Community Guidelines</TextLink>.',
            {
              TextLink: (children) => (
                <TextLink
                  href={buildCommunityGuidelinesURI({
                    locale: config.locale,
                    zone: config.zone,
                    experience: 'candidate',
                  })}
                  weight="weak"
                >
                  {children}
                </TextLink>
              ),
            },
          )}
        </Text>
        {formErrorComponent}
      </Stack>
    </ReviewPageLayout>
  );
};
